import React, { FC, useState, useEffect } from 'react';
import { Breakpoints } from '../../../../../constants/breakpoints.constants';
import useMediaQuery from '../../../../../lib/custom-hooks/useMediaQuery';
import { googleClickOpenVideoEvent } from '../../../../../utils/analytics';
import Picture from '../../../picture';
import { TContentHubImageProps } from '../../../picture/types/contentHubImage.types';
import { TEditorialImageProps } from '../../../picture/types/editorialImage.types';
import VideoPlayer from '../../../videoPlayer';
import { TMediaCardProps } from './types/mediaCard.types';

const MediaCard: FC<TMediaCardProps> = ({
  slide,
  playCallback,
  pauseCallback,
  endCallback,
  activeCard,
  moveLeft,
  moveRight,
  index,
  hasFixedAspectRatio,
  mediaHeightClassName,
  label,
}) => {
  const isWide = useMediaQuery(`(min-width: ${Breakpoints['md']}px)`);
  const [swipeStart, setSwipeStart] = useState(0);

  useEffect(() => {
    setSwipeStart(0);
  }, [activeCard, index]);

  const handleClickVideoPlay = (): void => {
    if (!isWide) return;
    googleClickOpenVideoEvent(label);
  };

  const handleTouchStartVideoPlay = (event): void => {
    if (isWide) return;
    const value = event?.changedTouches?.[0]?.pageX;
    if (!value) return;
    setSwipeStart(value);
  };

  const handleTouchEndVideoPlay = (event): void => {
    if (isWide) return;
    const value = event?.changedTouches?.[0]?.pageX;
    if (!value) {
      setSwipeStart(0);
      return;
    }

    if (value < swipeStart && swipeStart - value > 20) {
      if (moveRight) moveRight();
    } else if (value > swipeStart && value - swipeStart > 20) {
      if (moveLeft) moveLeft();
    }
    setSwipeStart(0);
  };

  const handleImage = (event): void => {
    if (!slide.isVideo) return;
    const img = event?.target;
    if (img.naturalWidth < 150 && img.src.includes('maxres')) {
      img.src = img.src.replace('maxres', 'hq');
    }
  };

  const productImageProps: TContentHubImageProps | undefined = slide?.productImageProps
    ? {
        onTouchStart: handleTouchStartVideoPlay,
        onTouchEnd: handleTouchEndVideoPlay,
        ...(slide.productImageProps || {}),
        onLoad: handleImage,
        pictureClassName: `media-carousel-card__picture ${hasFixedAspectRatio ? 'media-carousel-card__picture--16-9' : ''} ${
          slide.productImageProps?.pictureClassName ?? ''
        }`,
      }
    : undefined;

  const editorialImageProps: TEditorialImageProps | undefined = slide?.editorialImageProps
    ? {
        onTouchStart: handleTouchStartVideoPlay,
        onTouchEnd: handleTouchEndVideoPlay,
        desktopTransformation: 'Size1100',
        mobileTransformation: 'Size500',
        ...(slide.editorialImageProps || {}),
        cssClassPictureEl: `media-carousel-card__picture ${hasFixedAspectRatio ? 'media-carousel-card__picture--16-9' : ''} ${
          slide.editorialImageProps?.cssClassPictureEl ?? ''
        }`,
      }
    : undefined;

  return (
    <>
      <div className={'media-carousel-card__slide-wrapper ' + (mediaHeightClassName || '')}>
        {slide.isVideo ? (
          <div className="media-carousel-card__focus-image">
            {slide.isVideo && (
              <VideoPlayer
                link={slide.videoUrl?.value?.href}
                autoPlay={true}
                showThumbnail={true}
                muted={!isWide}
                playIconClickCallback={handleClickVideoPlay}
                playCallback={playCallback}
                pauseCallback={pauseCallback}
                endCallback={endCallback}
                editorialImageProps={editorialImageProps}
                productImageProps={productImageProps}
              />
            )}
          </div>
        ) : (
          <Picture productImageProps={productImageProps} editorialImageProps={editorialImageProps} />
        )}
      </div>
    </>
  );
};

export default React.memo(MediaCard);
